import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useOnboarding } from './contexts/OnboardingContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { QuickSaveProvider } from './contexts/QuickSaveContext';
import { VideoUploadsProvider } from './contexts/VideoUploadsContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { TourProvider, MilestoneCelebrationProvider, SmartPromptsProvider, BadgeUnlockCelebration } from './components/shared';
import NotificationService from './services/notificationService';
import ErrorBoundary from './components/ErrorBoundary';
import Navigation from './components/Navigation';
import BottomTabBar from './components/BottomTabBar';
import ApiUsageMonitor from './components/ApiUsageMonitor';
import ProtectedRoute from './components/ProtectedRoute';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import VerifyEmailBanner from './components/shared/VerifyEmailBanner';
import AccountStateBanner from './components/shared/AccountStateBanner';
import TerminatedAccountGate from './components/shared/TerminatedAccountGate';
import runTripMigration from './utils/runTripMigration';
import lazyWithRetry from './utils/lazyWithRetry';
// Home stays eager — it's the landing page
import Home from './pages/Home';
// Other routes lazy-loaded to shrink main bundle
const SearchResults = lazyWithRetry(() => import('./pages/SearchResults'));
const ImportSharedLink = lazyWithRetry(() => import('./pages/ImportSharedLink'));
const SocialFeed = lazyWithRetry(() => import('./pages/SocialFeed'));
const TripPlannerPage = lazyWithRetry(() => import('./pages/TripPlannerPage'));
const DayOutComposer = lazyWithRetry(() => import('./pages/DayOutComposer'));
const OwnProfile = lazyWithRetry(() => import('./pages/OwnProfile'));
const YouPage = lazyWithRetry(() => import('./pages/You'));
const CreatorStudio = lazyWithRetry(() => import('./pages/CreatorStudio'));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'));
const Welcome = lazyWithRetry(() => import('./pages/Welcome'));
const UserProfile = lazyWithRetry(() => import('./pages/UserProfile'));
const ActivityPlanner = lazyWithRetry(() => import('./pages/ActivityPlanner'));
const SharedTrip = lazyWithRetry(() => import('./pages/SharedTrip'));
const SharedTripCollection = lazyWithRetry(() => import('./pages/SharedTripCollection'));
const Collections = lazyWithRetry(() => import('./pages/Collections'));
const MySavedItems = lazyWithRetry(() => import('./pages/MySavedItems'));
const CollectionDetail = lazyWithRetry(() => import('./pages/CollectionDetail'));
const BoardPreview = lazyWithRetry(() => import('./pages/BoardPreview'));
const ActivityDetail = lazyWithRetry(() => import('./pages/ActivityDetail'));
const PlaceDetail = lazyWithRetry(() => import('./pages/PlaceDetail'));
const LabsTripMap = lazyWithRetry(() => import('./pages/LabsTripMap'));
const AlbumDetail = lazyWithRetry(() => import('./pages/AlbumDetail'));
const AlbumEdit = lazyWithRetry(() => import('./pages/AlbumEdit'));
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const SignIn = lazyWithRetry(() => import('./pages/SignIn'));
const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const WalkingTourDetail = lazyWithRetry(() => import('./pages/WalkingTourDetail'));
const WalkDetail = lazyWithRetry(() => import('./pages/WalkDetail'));
const WalkingTourEdit = lazyWithRetry(() => import('./pages/WalkingTourEdit'));
const TourismExperienceDetail = lazyWithRetry(() => import('./pages/TourismExperienceDetail'));
const EventGroupDetail = lazyWithRetry(() => import('./pages/EventGroupDetail'));
const Venue = lazyWithRetry(() => import('./pages/Venue'));
const WhatsOn = lazyWithRetry(() => import('./pages/WhatsOn'));
const Destination = lazyWithRetry(() => import('./pages/Destination'));
const DestinationBrowse = lazyWithRetry(() => import('./pages/DestinationBrowse'));
const Entity = lazyWithRetry(() => import('./pages/Entity'));
const AdvertiserPortal = lazyWithRetry(() => import('./pages/AdvertiserPortal'));
const ForBusinesses = lazyWithRetry(() => import('./pages/ForBusinesses'));
const Terms = lazyWithRetry(() => import('./pages/Terms'));
const IntimateImageRemoval = lazyWithRetry(() => import('./pages/IntimateImageRemoval'));
const Privacy = lazyWithRetry(() => import('./pages/Privacy'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const HowEnforcementWorks = lazyWithRetry(() => import('./pages/HowEnforcementWorks'));
const HandleProfile = lazyWithRetry(() => import('./pages/HandleProfile'));
const Messages = lazyWithRetry(() => import('./pages/Messages'));
const RateDay = lazyWithRetry(() => import('./pages/RateDay'));
const YourWeek = lazyWithRetry(() => import('./pages/YourWeek'));
/**
 * Destination pages remount on every kind/key change.
 *
 * One route pattern means React reuses the component instance when only the
 * params change, so ALL local state survives destination→destination
 * navigation: an in-flight "Plan a trip" would finish and yank the user into a
 * trip for the destination they just left, `dismissedIds` would filter cards on
 * the NEW page by ids dismissed on the old one, and render #1 at the new URL
 * paints the previous destination's hero and strips. Keying on the params is
 * one line and closes all three, where guarding each piece of state separately
 * would be three chances to miss one.
 */
function KeyedDestination() {
  const { kind, key } = useParams();
  return <Destination key={`${kind}:${key}`} />;
}

// Redirect old /shared/collection/ URLs to /collection/
function CollectionRedirect() {
  const { collectionId } = useParams();
  return <Navigate to={`/collection/${collectionId}`} replace />;
}

// Follow-digest action_url repairs. Both of these were produced by the nightly
// venue/artist alerts and neither is a route, so every digest already sent
// landed on NotFound. `action_url` is stored per notification row, so fixing
// the source alone would not rescue them — these redirects do, with no
// backfill: '/feed' was ONLY ever emitted by those two digests, so nothing
// else can be caught by it. See docs/WEEKEND_DISCOVERY_HANDOFF.md §1.
function EventGroupPluralRedirect() {
  const { groupId } = useParams();
  return <Navigate to={`/event-group/${groupId}`} replace />;
}

// Pages a signed-in person must reach with nothing in the way: someone asking
// for an intimate image to be removed may be an account holder whose terms are
// out of date, and a blocking modal over that form is the wrong first answer.
// The password-recovery pages open signed in too (BH-02-04), and a reset ends
// the session the modal would be asking about.
const TERMS_GATE_EXEMPT_PREFIXES = ['/intimate-image-removal', '/forgot-password', '/reset-password'];

// Shows terms acceptance modal when user's terms version is outdated
function TermsGate() {
  const { termsOutdated, acceptTerms, isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated || !termsOutdated) return null;
  if (TERMS_GATE_EXEMPT_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;
  return <TermsAcceptanceModal isOpen={true} onAccepted={acceptTerms} />;
}

// Routes that must never be hijacked by the first-run redirect: shared links
// sent to a signed-in user should open the thing that was shared, and the
// auth pages run their own flow. '/@' covers /@handle creator storefronts
// (the pathname is literally '/@name'; HandleProfile enforces the @).
const FIRST_RUN_EXEMPT_PREFIXES = [
  '/welcome', '/signin', '/signup', '/verify-email', '/forgot-password',
  '/reset-password', '/shared/', '/album/', '/board/', '/collection/',
  '/walking-tours/share/', '/tourism-experience/share/', '/place/',
  '/event-group/', '/import/maps-link', '/@', '/whats-on',
  '/terms', '/privacy', '/intimate-image-removal'
];

/**
 * Sends users who still need the first-run flow to /welcome, once.
 *
 * Covers new signups (SignUp already routes there directly) and existing
 * accounts with no home location and no follows — the ones whose feeds have
 * nothing to personalize against. Redirects at most once per browser session
 * per user, so backing out of the flow doesn't trap anyone in a loop, and
 * preserves where they were headed via returnTo.
 *
 * Waits for the ToS gate: being bounced to /welcome underneath a blocking
 * terms modal would leave the user unable to do either.
 */
function FirstRunGate() {
  const { isAuthenticated, currentUser, termsOutdated } = useAuth();
  const { needsFirstRun } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || termsOutdated || !needsFirstRun) return;
    const userId = currentUser?.id;
    if (!userId) return;
    if (FIRST_RUN_EXEMPT_PREFIXES.some(p => location.pathname.startsWith(p))) return;

    const key = `firstRunRedirected:${userId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      return; // sessionStorage blocked — don't risk a redirect loop
    }

    const returnTo = `${location.pathname}${location.search}`;
    navigate(`/welcome?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
  }, [isAuthenticated, termsOutdated, needsFirstRun, currentUser?.id, location.pathname, location.search, navigate]);

  return null;
}

function App() {
  useEffect(() => {
    // Initialize services when app starts
    NotificationService.init();

    // Make trip migration utility available globally (dev only)
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      window.runTripMigration = runTripMigration;
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
      <AuthProvider>
        <OnboardingProvider>
          <TourProvider>
            <MilestoneCelebrationProvider>
            <SmartPromptsProvider>
            <QuickSaveProvider>
            <VideoUploadsProvider>
              <Router>
            <div className="min-h-dvh bg-background">
              <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-primary focus:text-content-inverse focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium"
              >
                Skip to main content
              </a>
              <Navigation />
              <VerifyEmailBanner />
              <AccountStateBanner />
              <TermsGate />
              <FirstRunGate />
              <BadgeUnlockCelebration />
            {/* Phones with the tab bar: room for it under the last of the page
                (--app-tabbar-h is 0 everywhere else; index.css). */}
            <main id="main-content" className="pb-[var(--app-tabbar-h)]">
            <Suspense fallback={<div className="min-h-dvh" />}>
            <TerminatedAccountGate>
            <Routes>
          <Route path="/" element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          } />
          <Route path="/search-results" element={
            <ProtectedRoute>
              <SearchResults />
            </ProtectedRoute>
          } />
          <Route path="/import/maps-link" element={
            <ProtectedRoute>
              <ImportSharedLink />
            </ProtectedRoute>
          } />
          <Route path="/activity-planner" element={
            <ProtectedRoute>
              <ActivityPlanner />
            </ProtectedRoute>
          } />
          <Route path="/recommendations" element={<Navigate to="/" replace />} />
          <Route path="/shared/trip/:tripId" element={<SharedTrip />} />
          <Route path="/shared/trip/:tripId/collection/:collectionId" element={<SharedTripCollection />} />
          <Route path="/shared/collection/:collectionId" element={<CollectionRedirect />} />
          <Route path="/board/:boardId" element={<BoardPreview />} />
          <Route path="/social-feed" element={
            <ProtectedRoute>
              <SocialFeed />
            </ProtectedRoute>
          } />
          <Route path="/activity/:activityId" element={
            <ProtectedRoute>
              <ActivityDetail />
            </ProtectedRoute>
          } />
          <Route path="/place/:placeId" element={
            <ProtectedRoute>
              <PlaceDetail />
            </ProtectedRoute>
          } />
          {/* LABS — experimental map playground, deliberately unlinked. */}
          <Route path="/labs/trip-map" element={
            <ProtectedRoute>
              <LabsTripMap />
            </ProtectedRoute>
          } />
          <Route path="/album/:albumId/edit" element={
            <ProtectedRoute>
              <AlbumEdit />
            </ProtectedRoute>
          } />
          {/* Public route: shared PUBLIC recaps must render for signed-out
              recipients (the publish success screen's "Copy link" hands out
              this URL). The backend enforces visibility — private/friends
              albums 403 for anonymous viewers and the page shows its error
              state. Actions (like/comment/save) are signup-wall gated in
              AlbumDetail; /album/:albumId/edit above stays protected. */}
          <Route path="/album/:albumId" element={<AlbumDetail />} />
          <Route path="/collections" element={
            <ProtectedRoute>
              <Collections />
            </ProtectedRoute>
          } />
          <Route path="/my-saved-items" element={
            <ProtectedRoute>
              <MySavedItems />
            </ProtectedRoute>
          } />
          <Route path="/collection/:collectionId" element={<CollectionDetail />} />
          {/* First-run flow. Its own route (not a modal) so it can't stack
              with one, survives a reload, and can be resumed. */}
          <Route path="/welcome" element={
            <ProtectedRoute>
              <Welcome />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <OwnProfile />
            </ProtectedRoute>
          } />
          <Route path="/you" element={
            <ProtectedRoute>
              <YouPage />
            </ProtectedRoute>
          } />
          <Route path="/profile/:userId" element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          } />
          <Route path="/creator-studio" element={
            <ProtectedRoute>
              <CreatorStudio />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/signup" element={
            <ProtectedRoute requireAuth={false}>
              <SignUp />
            </ProtectedRoute>
          } />
          <Route path="/signin" element={
            <ProtectedRoute requireAuth={false}>
              <SignIn />
            </ProtectedRoute>
          } />
          <Route path="/verify-email" element={<VerifyEmail />} />
          {/* Open signed in too: the emailed link is the credential, and a
              remembered session must not discard it (BH-02-04). */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/advertise" element={
            <ProtectedRoute>
              <AdvertiserPortal />
            </ProtectedRoute>
          } />
          <Route path="/day-out/new" element={
            <ProtectedRoute>
              <DayOutComposer />
            </ProtectedRoute>
          } />
          <Route path="/trip/new" element={
            <ProtectedRoute>
              <TripPlannerPage />
            </ProtectedRoute>
          } />
          <Route path="/trip/:tripId" element={
            <ProtectedRoute>
              <TripPlannerPage />
            </ProtectedRoute>
          } />
          <Route path="/walking-tours/:tourId/edit" element={
            <ProtectedRoute>
              <WalkingTourEdit />
            </ProtectedRoute>
          } />
          <Route path="/walking-tours/:tourId" element={
            <ProtectedRoute>
              <WalkingTourDetail />
            </ProtectedRoute>
          } />
          <Route path="/walking-tours/share/:shareCode" element={<WalkingTourDetail />} />
          <Route path="/walks/:walkId" element={
            <ProtectedRoute>
              <WalkDetail />
            </ProtectedRoute>
          } />
          <Route path="/tourism-experience/:experienceId" element={
            <ProtectedRoute>
              <TourismExperienceDetail />
            </ProtectedRoute>
          } />
          <Route path="/tourism-experience/share/:shareCode" element={<TourismExperienceDetail />} />
          <Route path="/event-group/:groupId" element={
            <ProtectedRoute>
              <EventGroupDetail />
            </ProtectedRoute>
          } />
          <Route path="/venues/:venueId" element={<Venue />} />
          {/* Landing page for every follow digest. */}
          <Route path="/whats-on" element={
            <ProtectedRoute>
              <WhatsOn />
            </ProtectedRoute>
          } />
          {/* Repairs for already-sent notifications — see the redirect
              components above. Must stay ahead of the '/:handle' catch-all,
              which is what rendered NotFound for '/feed'. */}
          <Route path="/feed" element={<Navigate to="/whats-on" replace />} />
          <Route path="/event-groups/:groupId" element={<EventGroupPluralRedirect />} />
          {/* Destination overview. The kind lives in the path because a flat
              slug space already collides — regions.slug 'malta' and country MT
              are both live and both named Malta. Gated while the beta is
              closed; docs/DESTINATION_PAGE_SPEC.md §7 phase 5 opens it. */}
          <Route path="/destination/:kind/:key" element={
            <ProtectedRoute>
              <KeyedDestination />
            </ProtectedRoute>
          } />
          {/* Browse grid — rich-tier destinations only (spec §6 entry point 4).
              Route registered but not yet linked from navigation; where it
              surfaces is a product call. */}
          <Route path="/destinations" element={
            <ProtectedRoute>
              <DestinationBrowse />
            </ProtectedRoute>
          } />
          <Route path="/entities/:entityId" element={<Entity />} />
          <Route path="/for-businesses" element={<ForBusinesses />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/help/enforcement" element={<HowEnforcementWorks />} />
          <Route path="/intimate-image-removal" element={<IntimateImageRemoval />} />
          <Route path="/messages" element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          } />
          <Route path="/messages/:conversationId" element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          } />
          {/* "How was yesterday?" rating sheet — the activity_rating_nudge
              notification deep-links here. */}
          <Route path="/rate-day/:date" element={
            <ProtectedRoute>
              <RateDay />
            </ProtectedRoute>
          } />
          <Route path="/your-week/:weekStart" element={
            <ProtectedRoute>
              <YourWeek />
            </ProtectedRoute>
          } />
          {/* Vanity handle resolver. React Router v6 can't match partial
              segments — "/@:handle" NEVER matches (matchPath returns null) —
              so the whole "@name" segment is captured as :handle and
              HandleProfile enforces the leading "@" itself, rendering
              NotFound without it. Declared after all static routes; RRv6
              ranks static segments above dynamic ones anyway, so no
              top-level route can collide. Public (no ProtectedRoute
              wrapper) so IG-bio-link funnels work for anonymous viewers —
              mirrors /shared/trip/:tripId. */}
          <Route path="/:handle" element={<HandleProfile />} />
          <Route path="*" element={<NotFound />} />
            </Routes>
            </TerminatedAccountGate>
            </Suspense>
            </main>
            <BottomTabBar />
            {process.env.NODE_ENV !== 'production' && <ApiUsageMonitor />}
          </div>
        </Router>
            </VideoUploadsProvider>
            </QuickSaveProvider>
            </SmartPromptsProvider>
            </MilestoneCelebrationProvider>
          </TourProvider>
        </OnboardingProvider>
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
